import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useSelector } from "react-redux";
import {
  Ban,
  Briefcase,
  Building2,
  Eye,
  FolderKanban,
  Plus,
  RotateCcw,
  Trash2,
  TrendingUp,
  UserPlus,
  Users,
} from "lucide-react";
import PageShell from "../../components/dashboard/PageShell";
import FilteredDataTable from "../../components/dashboard/FilteredDataTable";
import InstitutionActionModal from "../../components/dashboard/InstitutionActionModal";
import OnboardingRequirementsButton from "../../components/dashboard/OnboardingRequirementsButton";
import { InstitutionCell } from "../../components/dashboard/tableCellContent";
import StatusBadge from "../../components/dashboard/StatusBadge";
import StatCard from "../../components/dashboard/StatCard";
import StatCardGrid from "../../components/dashboard/StatCardGrid";
import Button from "../../components/ui/Button";
import { USE_MOCK_AUTH } from "../../config/api";
import { INSTITUTIONS_FILTER_CONFIG } from "../../config/tableFilterConfigs";
import {
  clearInstitutionsForApiLoad,
  getInstitutions,
  hydrateInstitutionsFromApi,
  resetInstitutionsToLocal,
  subscribeInstitutions,
  updateInstitution,
} from "../../data/institutionsStore";
import { formatCurrency } from "../../data/mock/dashboardData";
import {
  clearInstitutionsCache,
  deleteInstitution,
  fetchInstitutions,
  fetchInstitutionsSummary,
  getInstitutionSlug,
  INSTITUTION_STATUS_LABELS,
  updateInstitutionStatus,
} from "../../services/institutionsApi";
import { navigateWithReturn } from "../../utils/navigationReturn";
import { showError, showSuccess } from "../../utils/toast";
import usePermissions from "../../hooks/usePermissions";
import useRole from "../../hooks/useRole";
import { OPS_PERMISSIONS as P } from "../../config/permissions";

function InstitutionStatusBadge({ status }) {
  const map = {
    active: { badge: "active", label: INSTITUTION_STATUS_LABELS.active },
    inactive: { badge: "inactive", label: INSTITUTION_STATUS_LABELS.inactive },
    deleted: { badge: "deleted", label: INSTITUTION_STATUS_LABELS.deleted },
    onboarding: { badge: "invited", label: INSTITUTION_STATUS_LABELS.onboarding },
  };
  const item = map[status] ?? { badge: "open", label: status };
  return <StatusBadge status={item.badge} label={item.label} />;
}

const EMPTY_SUMMARY = {
  totalInstitutions: 0,
  activeInstitutions: 0,
  totalActiveCases: 0,
  totalOutstanding: 0,
};

export default function InstitutionsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const token = useSelector((state) => state.auth.token);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;
  const { hasPermission } = usePermissions();
  const { isSuperAdmin, isExecutiveManager } = useRole();
  const canOnboard = hasPermission(P.INSTITUTIONS_ONBOARD);
  const canUpdate = hasPermission(P.INSTITUTIONS_UPDATE);
  const [institutions, setInstitutions] = useState(() => (USE_MOCK_AUTH ? getInstitutions() : []));
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [modal, setModal] = useState({ type: null, institution: null });
  const [loading, setLoading] = useState(!USE_MOCK_AUTH);
  const [actionLoading, setActionLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => subscribeInstitutions(() => setInstitutions([...getInstitutions()])), []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setLoadError(null);
      try {
        // Live mode is driven by env — never seed mock rows while waiting for a token/API.
        if (USE_MOCK_AUTH) {
          resetInstitutionsToLocal();
          if (!cancelled) {
            setInstitutions(getInstitutions());
            setSummary(EMPTY_SUMMARY);
          }
          return;
        }

        if (!token) {
          clearInstitutionsForApiLoad();
          if (!cancelled) {
            setInstitutions([]);
            setSummary(EMPTY_SUMMARY);
          }
          return;
        }

        // Drop leftover local/mock rows before the live response arrives.
        // Do not clearInstitutionsCache() here — StrictMode remounts must share the in-flight request.
        clearInstitutionsForApiLoad();
        if (!cancelled) setInstitutions([]);

        // Summary lives on its own endpoint; a failure there must not blank the table.
        const [listResult, summaryResult] = await Promise.allSettled([
          fetchInstitutions(token, { limit: 100 }),
          fetchInstitutionsSummary(token),
        ]);
        if (cancelled) return;

        if (listResult.status === "rejected") throw listResult.reason;

        hydrateInstitutionsFromApi(listResult.value?.institutions || []);
        setInstitutions([...getInstitutions()]);
        setSummary(
          summaryResult.status === "fulfilled"
            ? summaryResult.value || EMPTY_SUMMARY
            : EMPTY_SUMMARY
        );
      } catch (err) {
        if (!cancelled) {
          setLoadError(err.message || "Could not load institutions.");
          showError(err.message || "Could not load institutions.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const columns = useMemo(
    () => [
      { key: "numericId", header: "ID", type: "id" },
      {
        key: "name",
        header: "Institution",
        render: (row) => (
          <div className={row.status === "deleted" ? "opacity-70" : undefined}>
            <InstitutionCell name={row.name} institution={row} />
          </div>
        ),
      },
      { key: "regNumber", header: "Code", type: "id" },
      { key: "type", header: "Business type", type: "text" },
      { key: "region", header: "Region", type: "region" },
      { key: "activeCases", header: "Active cases", type: "number" },
      { key: "outstanding", header: "Outstanding", type: "currency" },
      {
        key: "status",
        header: "Status",
        type: "badge",
        render: (row) => <InstitutionStatusBadge status={row.status} />,
      },
      { key: "since", header: "Client since", type: "date" },
      {
        key: "actions",
        header: "Actions",
        align: "center",
        headerClassName: "text-center",
        actions: (row) => {
          const slug = encodeURIComponent(getInstitutionSlug(row) || row.id);
          const canMutateRow = (() => {
            if (!canUpdate) return false;
            if (isSuperAdmin) return true;
            if (isExecutiveManager) return row.canManage === true;
            return true;
          })();
          return [
            {
              key: "details",
              label: "View details",
              description: "Profile, contacts and configuration",
              icon: Eye,
              tone: "primary",
              to: `/dashboard/institutions/${row.id}`,
            },
            {
              key: "customers",
              label: "View customers",
              description: "Debtors linked to this institution",
              icon: Users,
              tone: "emerald",
              to: `/dashboard/institutions/${slug}/customers`,
              hidden: row.status === "deleted",
            },
            {
              key: "groups",
              label: "View portfolio groups",
              description: "Groups, uploads, cases and agent assignment",
              icon: FolderKanban,
              tone: "sky",
              to: `/dashboard/institutions/${slug}/portfolio-groups`,
              hidden: row.status === "deleted",
            },
            row.status === "deleted"
              ? {
                  key: "restore",
                  label: "Restore institution",
                  description: "Clear soft-delete, set active, and reopen portal login",
                  icon: RotateCcw,
                  tone: "emerald",
                  onClick: () => setModal({ type: "restore", institution: row }),
                  hidden: !canMutateRow,
                }
              : row.status === "active"
                ? {
                    key: "deactivate",
                    label: "Deactivate",
                    description: "Set status to inactive",
                    icon: Ban,
                    tone: "amber",
                    onClick: () => setModal({ type: "deactivate", institution: row }),
                    hidden: !canMutateRow,
                  }
                : {
                    key: "activate",
                    label: "Activate",
                    description: "Set status to active",
                    icon: UserPlus,
                    tone: "emerald",
                    onClick: () => setModal({ type: "activate", institution: row }),
                    hidden: !canMutateRow,
                  },
            {
              key: "remove",
              label: "Delete institution",
              description: "Soft-delete this institution (can restore later)",
              icon: Trash2,
              tone: "red",
              onClick: () => setModal({ type: "remove", institution: row }),
              hidden: !canMutateRow || row.status === "deleted",
            },
          ].filter((action) => !action.hidden);
        },
      },
    ],
    [canUpdate, isSuperAdmin, isExecutiveManager]
  );

  const sortedInstitutions = useMemo(() => {
    const rows = [...institutions];
    rows.sort((a, b) => {
      const aDeleted = a.status === "deleted" || a.deleted_at ? 1 : 0;
      const bDeleted = b.status === "deleted" || b.deleted_at ? 1 : 0;
      if (aDeleted !== bDeleted) return aDeleted - bDeleted;
      return 0;
    });
    return rows;
  }, [institutions]);

  const handleModalConfirm = async () => {
    const { type, institution } = modal;
    if (!institution) return;

    const slug = getInstitutionSlug(institution);
    const rowKey = institution.id || institution.provider_slug || institution.numericId || slug;
    setActionLoading(true);
    try {
      if (type === "deactivate") {
        const updated = await updateInstitutionStatus(token, slug, "inactive");
        clearInstitutionsCache();
        updateInstitution(rowKey, {
          ...(updated || {}),
          status: "inactive",
          deleted_at: null,
        });
        setInstitutions([...getInstitutions()]);
        showSuccess("Institution deactivated.");
      } else if (type === "activate") {
        const updated = await updateInstitutionStatus(token, slug, "active");
        clearInstitutionsCache();
        updateInstitution(rowKey, {
          ...(updated || {}),
          status: "active",
          deleted_at: null,
          lastActivity: new Date().toISOString().slice(0, 10),
        });
        setInstitutions([...getInstitutions()]);
        showSuccess("Institution activated.");
      } else if (type === "restore") {
        const updated = await updateInstitutionStatus(token, slug, "restore");
        clearInstitutionsCache();
        updateInstitution(rowKey, {
          ...(updated || {}),
          status: "active",
          deleted_at: null,
          lastActivity: new Date().toISOString().slice(0, 10),
        });
        setInstitutions([...getInstitutions()]);
        showSuccess("Institution restored.");
      } else if (type === "remove") {
        const updated = await deleteInstitution(token, slug);
        clearInstitutionsCache();
        // Force deleted status even if the API body is thin/missing.
        updateInstitution(rowKey, {
          ...(updated || {}),
          status: "deleted",
          deleted_at: updated?.deleted_at || new Date().toISOString(),
        });
        setInstitutions([...getInstitutions()]);
        showSuccess("Institution deleted.");
      }

      // Refresh counts from API when available.
      if (useLiveApi && token) {
        fetchInstitutionsSummary(token)
          .then((next) => next && setSummary(next))
          .catch(() => {});
      }
    } catch (error) {
      showError(error.message || "Could not update institution.");
      throw error;
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <PageShell
      title="Institutions"
      description={
        isSuperAdmin || isExecutiveManager
          ? "Service provider clients on SimCollect — soft-deleted institutions stay listed so you can restore portal access."
          : "Institutions assigned to you."
      }
      actions={
        canOnboard ? (
          <div className="flex flex-wrap items-center gap-2">
            <OnboardingRequirementsButton />
            <Button
              variant="accent"
              size="sm"
              onClick={() => navigateWithReturn(navigate, "/dashboard/institutions/onboard", location)}
            >
              <Plus className="h-4 w-4" />
              Onboard institution
            </Button>
          </div>
        ) : null
      }
    >
      {loading ? (
        <div className="mb-3 rounded-xl border border-primary/[0.07] bg-primary/[0.02] px-4 py-3 text-xs text-primary/50">
          Loading institutions…
        </div>
      ) : null}
      {loadError && !loading ? (
        <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-800">
          {loadError}
        </div>
      ) : null}

      <StatCardGrid cols={4}>
        <StatCard
          label="Institutions"
          value={summary.totalInstitutions}
          icon={Building2}
          tone="primary"
        />
        <StatCard
          label="Active clients"
          value={summary.activeInstitutions}
          icon={UserPlus}
          tone="emerald"
        />
        <StatCard
          label="Active cases"
          value={summary.totalActiveCases}
          icon={Briefcase}
          tone="accent"
        />
        <StatCard
          label="Total outstanding"
          value={formatCurrency(summary.totalOutstanding)}
          icon={TrendingUp}
          tone="primary"
        />
      </StatCardGrid>

      <FilteredDataTable
        data={sortedInstitutions}
        columns={columns}
        config={{ ...INSTITUTIONS_FILTER_CONFIG, title: "Institution filters" }}
        pageSize={10}
        emptyMessage="No institutions yet. Onboard a service provider to get started."
      />

      <InstitutionActionModal
        type={modal.type}
        institution={modal.institution}
        loading={actionLoading}
        onConfirm={handleModalConfirm}
        onClose={() => setModal({ type: null, institution: null })}
      />
    </PageShell>
  );
}
